import { Component, EventEmitter, InjectionToken, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker, MatDateRangePicker } from '@angular/material/datepicker';
import { DateMatDateFormat, DateType, DateTypes } from '../../models/date-types';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';

import * as moment from 'moment';
import { Moment } from 'moment';

export const DATE_FORMAT_TOKEN = new InjectionToken<any>('DATE_FORMAT_TOKEN');

export function getDateFormats(dateType: DateType, dateFormat?: MatDateFormats) {
  return (dateFormat ? dateFormat : DateTypes[dateType]) || DateMatDateFormat;
}

@Component({
  selector: 'smt-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  providers: [
    {
      provide: DATE_FORMAT_TOKEN,
      useFactory: (component: DateInputComponent) => {
        const { dateType, dateFormat } = component;
        return { dateType, dateFormat };
      },
      deps: [DateInputComponent],
    },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (dateFormatToken: { dateType: DateType, dateFormat?: MatDateFormats }) => getDateFormats(dateFormatToken.dateType, dateFormatToken.dateFormat),
      deps: [DATE_FORMAT_TOKEN],
    }
  ]
})
export class DateInputComponent implements OnInit {

  @Output() dateChanged = new EventEmitter<void>();

  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() placeholder: string;
  @Input() extraControlName: string;
  @Input() extraPlaceholder: string;
  @Input() label: string;
  @Input() hint: string;
  @Input() dateType: DateType = DateType.YEAR;
  @Input() dateFormat: MatDateFormats;
  @Input() readonly: boolean;
  @Input() color: string;
  @Input() minDate: Date;
  @Input() maxDate: Date;

  DateType: typeof DateType = DateType;

  control: AbstractControl<any, any>;
  extraControl: AbstractControl<any, any>;

  dateTimeTime;

  constructor() {
    this.readonly = false;
  }

  ngOnInit(): void {
    this.control = this.group?.get(this.controlName);
    if (this.extraControlName) {
      this.extraControl = this.group?.get(this.extraControlName);
    }

    // Para el rango de mes y año, enlazar los dos inputs
    if (this.rangeToInputs) {
      const startControl = this.group.get(this.controlName);
      const endControl = this.group.get(this.extraControlName);

      startControl?.valueChanges.subscribe(start => {
        const end = endControl?.value;
        if (start && end && start.isAfter(end)) {
          endControl?.setValue(start, { emitEvent: false });
        }
      });

      endControl?.valueChanges.subscribe(end => {
        const start = startControl?.value;
        if (start && end && end.isBefore(start)) {
          startControl?.setValue(end, { emitEvent: false });
        }
      });
    }

    if (this.control?.value) {
      const value = moment(this.control.value);
      this.dateTimeTime = value.format('HH:mm');
    }
  }

  isRequired(controlName: string, formGroup: FormGroup<any>): boolean {
    const control = formGroup.controls[controlName];
    return control.hasValidator(Validators.required);
  }

  openDatepicker(datepicker: MatDatepicker<Moment> | MatDateRangePicker<any>) {
    if (!datepicker.opened) {
      datepicker.open();
    }
  }

  selectedHandler(controlName: string, chosenDate: Moment, datepicker: MatDatepicker<Moment>, dateType: DateType) {
    const type = this.dateType === DateType.RANGE_YEAR ? DateType.YEAR
      : this.dateType === DateType.RANGE_MONTH || this.dateType === DateType.MONTH_ONLY ? DateType.MONTH
        : this.dateType;

    type === dateType ? datepicker.close() : null;
    chosenDate.set({ date: 1 });

    this.group.get(controlName).setValue(chosenDate, { emitEvent: this.rangeToInputs ? true : false });
    this.dateChanged.emit();
  }

  remove(control?: AbstractControl<any, any>) {
    this.dateTimeTime = null;
    if(control) {
      control.reset();
    } else  {
      this.control.reset();
      this.extraControl?.reset();
    }
    this.dateChanged.emit();
  }

  onDateChanged(): void {
    const value = this.control?.value;

    if (value && this.dateTimeTime) {
      const date = new Date(value);
      const [hours, minutes] = this.dateTimeTime.split(':').map(Number);

      date.setHours(hours, minutes, 0, 0);

      this.control.setValue(date);
    }

    this.updateDateTimeTime();
    this.dateChanged.emit();
  }

  private updateDateTimeTime(): void {
    const value = this.control?.value;

    if (value) {
      this.dateTimeTime = moment(value).format('HH:mm');
    } else {
      this.dateTimeTime = '';
    }
  }

  timeChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    const time = input.value; // HH:mm

    if (!time || !this.control.value) {
      return;
    }

    const date = new Date(this.control.value);
    const [hours, minutes] = time.split(':').map(Number);

    date.setHours(hours, minutes, 0, 0);

    this.control.setValue(date);
    this.control.markAsDirty();
    this.control.markAsTouched();

    this.dateChanged.emit();
  }

  get rangeToInputs() {
    return this.extraControlName && this.dateType !== DateType.RANGE;
  }

}
